// Preserve Shopify's mutation/result contract; serialize it through the existing Cart owner.
(() => {
  if (window.AplovioShopifyActions) return;
  const configured = new WeakSet();
  let library, loading;
  async function loadEvents() {
    if (library) return library;
    if (!loading) loading = import('https://cdn.shopify.com/storefront/standard-events.js')
      .then(value => { library = value; return value; })
      .catch(() => { loading = null; return null; });
    let timer;
    try { return await Promise.race([loading, new Promise(resolve => { timer = setTimeout(() => resolve(null), 2000); })]); }
    finally { clearTimeout(timer); }
  }
  async function createNative(cart, url, options) {
    if (options.method !== 'POST' || !/\/cart\/(add|change|update)\.js$/.test(url.pathname)) return null;
    const events = await loadEvents();
    if (!events) return null;
    const data = options.body instanceof FormData ? Object.fromEntries(options.body) : JSON.parse(options.body);
    let EventClass, payload;
    if (url.pathname.endsWith('/add.js')) {
      EventClass = events.CartLinesUpdateEvent;
      payload = {action: 'add', context: options.eventSource?.closest('aplovio-product') ? 'product' : cart.page ? 'cart' : 'dialog',
        lines: (data.items || [data]).map(line => ({merchandiseId: String(line.id), quantity: Number(line.quantity || 1)}))};
    } else if (url.pathname.endsWith('/change.js')) {
      EventClass = events.CartLinesUpdateEvent;
      payload = {action: Number(data.quantity) === 0 ? 'remove' : 'update', context: cart.page ? 'cart' : 'dialog',
        lines: [{id: String(data.id), quantity: Number(data.quantity)}]};
    } else if ('note' in data) {
      EventClass = events.CartNoteUpdateEvent;
      payload = {context: cart.page ? 'cart' : 'dialog', note: data.note};
    } else if ('discount' in data) {
      EventClass = events.CartDiscountUpdateEvent;
      payload = {discountCodes: data.discount.split(',').filter(Boolean).map(code => ({code}))};
    }
    if (!EventClass) return null;
    const deferred = EventClass.createPromise();
    deferred.promise.catch(() => {});
    const target = options.eventSource?.isConnected ? options.eventSource : cart;
    target.dispatchEvent(new EventClass({...payload, promise: deferred.promise}));
    return {cart, target, EventClass, deferred};
  }
  async function beginNative(cart, url, options) {
    try { return await createNative(cart, url, options); }
    catch { document.dispatchEvent(new CustomEvent('aplovio:apps-error', {detail: {code: 'standard_event_unavailable'}})); return null; }
  }
  async function settleNative(operation, payload, error) {
    if (!operation) return;
    const {cart, target, EventClass, deferred} = operation;
    try {
      if (error && !error.definite) throw error;
      const state = Array.isArray(payload?.items) ? payload : await cart.readState();
      deferred.resolve({cart: EventClass.createCartFromAjaxResponse(state),
        userErrors: error ? [{code: 'INVALID', field: [], message: error.message}] : [], warnings: []});
    } catch (failure) {
      deferred.reject(failure);
      target.dispatchEvent(new library.CartErrorEvent({error: failure.message, code: 'SERVICE_UNAVAILABLE'}));
    }
  }
  function configure() {
    const actions = window.Shopify?.actions;
    if (!document.querySelector('aplovio-cart') || !window.AplovioCartEntry) return false;
    if (!actions || configured.has(actions)) return false;
    if (typeof actions.updateCart?.configure !== 'function' || typeof actions.openCart?.configure !== 'function') return false;
    configured.add(actions);
    const update = actions.updateCart.configure({
      eventTarget: () => document.querySelector('aplovio-cart') || document,
      async handler(defaultHandler, payload, options) {
        const cart = await window.AplovioCartEntry.load();
        return cart.externalUpdate(defaultHandler, options);
      }
    });
    const open = actions.openCart.configure({
      async handler(defaultHandler) {
        if (!document.querySelector('aplovio-cart')) return defaultHandler();
        const cart = await window.AplovioCartEntry.load();
        await cart.show(document.activeElement);
      }
    });
    document.dispatchEvent(new CustomEvent('aplovio:apps-cart-bridge', {detail: {update, open}}));
    return update !== false && open !== false;
  }
  window.AplovioShopifyActions = Object.freeze({configure, loadEvents, beginNative, settleNative});
  if (document.readyState !== 'complete') document.addEventListener('DOMContentLoaded', configure, {once: true});
  else configure();
})();
